import { format } from 'date-fns';
import { useTheme, Box, Typography, Paper, Divider } from '@mui/material';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexCenter from 'components/FlexCenter';


const StatCell = ({ label, value }) => (
    <Box>
        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
        <Typography variant="body2" fontWeight="medium">{value ?? '—'}</Typography>
    </Box>
);

const RouteCard = ({ row, index, timeMode, minCrewRouteTAT }) => {
    const theme = useTheme();
    const routeDateDisplay = row.routeDate ? format(new Date(row.routeDate), 'dd/MM/yy') : '—';

    return (
        <Paper 
            elevation={1} 
            sx={{ 
                p: 1.5, 
                borderLeft: `2px solid ${theme.palette.primary.main}`, 
                borderRadius: 1 
            }}
        >
            <Box display="flex" alignItems="flex-start" gap={1}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    #{index + 1}
                </Typography>
                <Box flex={1} minWidth={0}>
                    <Typography variant="subtitle2" fontWeight="bold" noWrap>
                        {routeDateDisplay} · {row.routeNo} · {row.crewRoute || `${row.dep}→${row.arr}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {row.acType} · {row.flightReg}
                    </Typography>
                </Box>
            </Box>

            {Array.isArray(row.routeDetails) && row.routeDetails.length > 0 && (
                <Box mt={0.75}>
                    {row.routeDetails.map((flight, i) => {
                        const atd = timeMode === 'UTC'
                            ? (flight?.atd || flight?.etd)
                            : (flight?.atdLocalBase || flight?.etdLocalBase);
                        const ata = timeMode === 'UTC'
                            ? (flight?.ata || flight?.eta)
                            : (flight?.ataLocalBase || flight?.etaLocalBase);
                        const isWarning = flight.diffRegScheduleTat !== null &&
                            flight.diffRegScheduleTat < minCrewRouteTAT;

                        return (
                            <Box key={i} display="flex" alignItems="center" gap={0.75}
                                sx={{ color: isWarning ? theme.palette.warning.main : 'inherit' }}>
                                <Typography variant="caption" sx={{ minWidth: 28, textAlign: 'right', fontFamily: 'monospace' }}>
                                    {flight.flightNo}
                                </Typography>
                                <Typography variant="caption" sx={{ minWidth: 48, textAlign: 'center', fontFamily: 'monospace' }}>
                                    {flight.flightReg || flight.acType}
                                </Typography>
                                <Typography variant="caption" sx={{ minWidth: 36, fontFamily: 'monospace' }}>
                                    {atd}
                                </Typography>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                    {flight.dep}→{flight.arr}
                                </Typography>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                    {ata}
                                </Typography>
                                {flight.pax && (
                                    <Typography variant="caption" color="text.secondary">{flight.pax}</Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}

            <Divider sx={{ my: 0.75 }} />

            <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={0.75}>
                <StatCell label="BH" value={row.blockHours > 0 ? parseFloat(row.blockHours).toFixed(2) : 0} />
                <StatCell label="Sectors" value={row.sectors ?? 0} />
                <StatCell label="Elapsed" value={row.elapsedHours > 0 ? parseFloat(row.elapsedHours).toFixed(2) : 0} />
                <StatCell label="Perdiem" value={row.perdiemMinutes > 0 ? Math.round(row.perdiemMinutes) : 0} />
            </Box>

            {(row.crewCount > 0 || row.cLounge > 0 || row.hotelDays > 0) && (
                <Box display="flex" gap={1.5} mt={0.5} flexWrap="wrap">
                    {row.crewCount > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            Crew: <strong>{row.crewCount}</strong> (FC:{row.fcCount} CC:{row.ccCount})
                        </Typography>
                    )}
                    {row.cLounge > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            CLounge: <strong>{row.cLounge}</strong>
                        </Typography>
                    )}
                    {row.hotelDays > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            Hotel: <strong>{row.hotelDays}d</strong>
                        </Typography>
                    )}
                </Box>
            )}
        </Paper>
    );
};


const CrewRoutesList = ({ rows, timeMode, minCrewRouteTAT }) => {
    if (!rows?.length) {
        return (
            <FlexCenter height={LAYOUT_HEIGHT}>
                <Typography color="error">No data available</Typography>
            </FlexCenter>
        );
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            width="100%"
            gap={2}
        >
            {rows.map((row, index) => (
                <RouteCard
                    key={row.key}
                    row={row}
                    index={index}
                    timeMode={timeMode}
                    minCrewRouteTAT={minCrewRouteTAT}
                />
            ))}
        </Box>
    );
};

export default CrewRoutesList;
